import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  userRole: string | null;
  repairUserProfile: (fullName: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    // Initial session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("Initial session check:", session);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
      }
      setIsLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log("Auth state changed:", { event: _event, session });
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserRole(session.user.id);
      } else {
        setUserRole(null);
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserRole = async (userId: string) => {
    console.log("Fetching user role for:", userId);
    const { data, error } = await supabase
      .from("profiles")
      .select("role")
      .eq("user_id", userId)
      .single();

    if (error) {
      console.error("Error fetching user role:", error);
      return;
    }

    console.log("User role data:", data);
    setUserRole(data?.role ?? "staff");
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    try {
      console.log("Starting sign out process...");
      const { error } = await supabase.auth.signOut();
      if (error) {
        // If the session is already missing, that's actually what we want
        if (error.message?.includes("Auth session missing")) {
          console.log("Session already cleared, sign out successful");
          // Manually clear the local state
          setSession(null);
          setUser(null);
          setUserRole(null);
          return;
        }
        console.error("Sign out error:", error);
        throw error;
      }
      console.log("Sign out successful");
    } catch (error: any) {
      // Handle the case where session is already missing
      if (
        error.message?.includes("Auth session missing") ||
        error.message?.includes("AuthSessionMissingError")
      ) {
        console.log("Session already cleared, treating as successful sign out");
        // Manually clear the local state
        setSession(null);
        setUser(null);
        setUserRole(null);
        return;
      }
      console.error("Error during sign out:", error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  // Utility function to repair orphaned users (users with auth but no profile)
  const repairUserProfile = async (fullName: string) => {
    try {
      if (!user) {
        throw new Error("No authenticated user found");
      }

      console.log("Attempting to repair profile for user:", user.id);

      const { error: profileError } = await supabase.from("profiles").upsert(
        {
          user_id: user.id,
          full_name: fullName,
          role: "staff",
        },
        {
          onConflict: "user_id",
          ignoreDuplicates: false,
        }
      );

      if (profileError) {
        console.error("Profile repair error:", profileError);
        throw profileError;
      }

      // Refresh user role after repair
      await fetchUserRole(user.id);

      console.log("Profile repair completed successfully");
      return { error: null };
    } catch (error: any) {
      console.error("User profile repair error:", error);
      return { error };
    }
  };

  const value = {
    user,
    session,
    isLoading,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    userRole,
    repairUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
